import { useEffect, useState } from 'react';

import { startOfToday } from 'date-fns';
import { MuiTelInput } from 'mui-tel-input';
import { useTranslation } from 'react-i18next';

import { CLA, PlanUnit, RosterFunction } from 'components/types';

import { toLocalISOString } from 'utils/date';

import { getPlanUnits } from 'RestAPIs/cost-centers';
import { createNewEmployee, EmployeePOSTResponse } from 'RestAPIs/employees';
import { getClas } from 'RestAPIs/employees/clas';
import { getRosterFunctions } from 'RestAPIs/roster/functions';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

import {
  DatePicker,
  Dialog,
  EmailField,
  OptionalEndDate,
  showSnackbar,
  VirtualizedAutocomplete
} from 'components/shared';

import CreateEmployeeDialogStyles from './CreateEmployeeDialogStyles';
import {
  GridCompact2,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';

type DialogProps = {
  open?: true;
  onHidden: () => void;
  onCreate: (employee: EmployeePOSTResponse) => void;
};

export default function CreateEmployeeDialog({
  open = true,
  onCreate,
  onHidden
}: DialogProps) {
  const { t } = useTranslation('common');
  const [hide, setHide] = useState(false);

  const [planUnits, setPlanUnits] = useState<PlanUnit[]>([]);
  const [functions, setFunctions] = useState<RosterFunction[]>([]);
  const [clas, setClas] = useState<CLA[]>([]);

  const [firstName, setFirstName] = useState('');
  const [infix, setInfix] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [hourlyRate, setHourlyRate] = useState<number | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | null>(startOfToday());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedPlanunit, setSelectedPlanunit] = useState<PlanUnit | null>(
    null
  );
  const [selectedFunction, setSelectedFunction] =
    useState<RosterFunction | null>(null);
  const [selectedCla, setSelectedCla] = useState<CLA | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isSaveBtnDisabled =
    isSaving ||
    [
      firstName,
      lastName,
      emailAddress,
      startDate,
      selectedPlanunit,
      selectedFunction,
      selectedCla
    ].some((v) => (Array.isArray(v) ? !v.length : !v));

  const handleClose = () => {
    setHide(true);
  };

  const handleSave = () => {
    if (!startDate || !selectedPlanunit || !selectedFunction || !selectedCla) {
      return;
    }
    setIsSaving(true);
    createNewEmployee({
      firstName,
      infix: infix || undefined,
      lastName,
      phoneNumber: phoneNumber || undefined,
      emailAddress,
      hourlyRate,
      startDate: toLocalISOString(startDate),
      endDate: endDate ? toLocalISOString(endDate) : undefined,
      selectedPlanunit: selectedPlanunit.id,
      selectedFunction: selectedFunction.id,
      selectedCla: selectedCla.claId
    }).then(({ data }) => {
      onCreate(data);
      showSnackbar({
        message: t('changesSaved'),
        severity: 'success'
      });
      handleClose();
    });
  };

  useEffect(() => {
    if (startDate) {
      const interval = { startDate, endDate: endDate || startDate };
      getPlanUnits(interval).then(({ data }) => {
        setPlanUnits(data);
        if (data.length === 1) {
          setSelectedPlanunit(data[0]);
        }
      });
      getRosterFunctions(interval).then(({ data }) => {
        setFunctions(data);
        if (data.length === 1) {
          setSelectedFunction(data[0]);
        }
      });
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (selectedPlanunit && startDate) {
      getClas(selectedPlanunit.id, toLocalISOString(startDate)).then(
        ({ data }) => {
          setClas(data);
          if (!data.length) {
            setSelectedCla(null);
          } else if (data.length === 1) {
            setSelectedCla(data[0]);
          }
        }
      );
    } else {
      setClas([]);
      setSelectedCla(null);
    }
  }, [selectedPlanunit, startDate]);

  return (
    <Dialog
      title={t('createNewEmployee')}
      handleClose={handleClose}
      TransitionProps={{ onExited: onHidden }}
      open={hide ? false : open}
    >
      <CreateEmployeeDialogStyles>
        <div className="section">
          <h3>{t('contactInfo')}</h3>
          <GridContainer>
            <GridCompact6>
              <TextField
                label={t('firstName')}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                required
              />
            </GridCompact6>
            <GridCompact2>
              <TextField
                label={t('infix')}
                value={infix}
                onChange={(e) => setInfix(e.target.value)}
                fullWidth
              />
            </GridCompact2>
            <GridCompact6>
              <TextField
                label={t('lastName')}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                required
              />
            </GridCompact6>
            <GridCompact6>
              <EmailField
                setEmailValue={(value) => setEmailAddress(value)}
                required
              />
            </GridCompact6>
            <GridCompact6>
              <MuiTelInput
                label={t('phoneNumber')}
                defaultCountry="NL"
                continents={['EU']}
                value={phoneNumber}
                onChange={(value) => setPhoneNumber(value.replace(/\s/g, ''))}
                fullWidth
              />
            </GridCompact6>
          </GridContainer>
        </div>
        <div className="section">
          <h3>{t('workAgreement')}</h3>
          <GridContainer>
            <GridCompact6>
              <DatePicker
                date={startDate}
                onChange={(date) => setStartDate(date)}
                label={t('startDate')}
                required
              />
            </GridCompact6>
            <GridCompact6 className="d-flex v-bottom">
              <OptionalEndDate
                date={endDate}
                onDateChanged={(date) => {
                  setEndDate(date);
                }}
                hideRemove
              />
            </GridCompact6>
            <GridCompact6>
              <VirtualizedAutocomplete
                value={selectedPlanunit}
                options={planUnits}
                getOptionLabel={(o) => {
                  if (typeof o === 'string') {
                    return o;
                  }

                  return o.name;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('organizationalUnit')}
                    required
                  />
                )}
                onChange={(e, value) => {
                  setSelectedPlanunit(value as PlanUnit);
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                autoHighlight
              />
            </GridCompact6>
            <GridCompact6>
              <VirtualizedAutocomplete
                value={selectedCla}
                options={clas}
                optionLabelProp="code"
                getOptionLabel={(o) => {
                  if (typeof o === 'string') {
                    return o;
                  }

                  return o.code;
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('CLA')} required />
                )}
                onChange={(e, value) => {
                  setSelectedCla(value as CLA);
                }}
                isOptionEqualToValue={(a, b) => a.claId === b.claId}
                autoHighlight
                disabled={!selectedPlanunit}
              />
            </GridCompact6>
            <GridCompact6>
              <VirtualizedAutocomplete
                value={selectedFunction}
                options={functions}
                optionLabelProp="description"
                getOptionLabel={(o) => {
                  if (typeof o === 'string') {
                    return o;
                  }

                  return o.description;
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('function')} required />
                )}
                onChange={(e, value) => {
                  setSelectedFunction(value as RosterFunction);
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                autoHighlight
              />
            </GridCompact6>
            <GridCompact6>
              <TextField
                label={t('hourlyRate')}
                type="number"
                value={hourlyRate === null ? '' : hourlyRate}
                onChange={({ target }) => {
                  const { value } = target;
                  const parsed = parseFloat(value.replace(/-/g, ''));
                  setHourlyRate(
                    Number.isNaN(parsed)
                      ? undefined
                      : parseFloat(parsed.toFixed(2))
                  );
                }}
                slotProps={{
                  input: {
                    inputProps: {
                      min: 0
                    }
                  }
                }}
                fullWidth
              />
            </GridCompact6>
          </GridContainer>
        </div>
      </CreateEmployeeDialogStyles>
      <DialogActions>
        <Button
          onClick={() => setHide(true)}
          color="secondary"
          variant="contained"
        >
          {t('close')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaveBtnDisabled}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
